import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getApiKeyMutationKey } from '.';
import { apiKeyService } from '../../application/ApiKeyService';
import { OrganizationActionType } from '../../context/organization/actions';
import { useOrganizationContext } from '../../context/organization/context';
import { ApiKey } from '../../domain/apiKey/ApiKey';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

type UpdateApiKeyParams = { apiKey: ApiKey };
type UpdateApiKeyResponse = ServiceResponse<(params: UpdateApiKeyParams) => void, ApiKey>;

// Hooks act as 'Adapter' layer.
export const useUpdateApiKey = (): UpdateApiKeyResponse => {
  const queryClient = useQueryClient();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { organizationState, organizationDispatch } = useOrganizationContext();

  const { isLoading, isError, isSuccess, mutate } = useMutation(
    ({ apiKey }: UpdateApiKeyParams): Promise<ApiKey> => apiKeyService.update({ apiKey }),
    {
      onMutate: ({ apiKey }: UpdateApiKeyParams) => {
        const newApiKey = organizationState.apiKeys.find((ak) => ak.id === apiKey.id);

        if (!newApiKey) {
          return;
        }
        newApiKey.label = apiKey.label;
        newApiKey.active = apiKey.active;

        organizationDispatch({
          type: OrganizationActionType.FETCH_APIKEYS_SUCCESS,
          payload: { apiKeys: [...organizationState.apiKeys.filter((ak) => ak.id !== apiKey.id), newApiKey] },
        });
        return;
      },
      onError: (err: any) => {
        queryClient.invalidateQueries(getApiKeyMutationKey());
        console.info(`ApiKey update error: `, err?.response);
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading,
    run: (params: UpdateApiKeyParams) => mutate(params),
  };
};
